import { forwardRef, memo } from 'react';
import { FormattedMessage } from 'react-intl';
// Material UI imports
import type { SxProps, Theme } from '@mui/material';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
// Skillmore UI Components
import { shape } from '@empathco/ui-components/src/styles/themeOptions';

export type OpportunityMatchesCalculatingVariant = 'inline' | 'notice';

const contentSx: SxProps<Theme> = { display: 'inline-flex', alignItems: 'center', verticalAlign: 'top', gap: 1.5 } as const;
const noticeSx: SxProps<Theme> = {
  p: shape.cardPadding,
  typography: 'subtitle1',
  fontWeight: 'fontWeightRegular',
  '& .MuiAlert-message': { textAlign: 'center', flexGrow: 1 }
} as const;
const retryButtonSx: SxProps<Theme> = { whiteSpace: 'nowrap', flexShrink: 0 } as const;

type OpportunityMatchesCalculatingProps = {
  readonly variant?: OpportunityMatchesCalculatingVariant;
  readonly timedOut?: boolean;
  readonly onRetry?: () => void;
};

const OpportunityMatchesCalculating = forwardRef<HTMLDivElement, OpportunityMatchesCalculatingProps>(({
  variant = 'inline',
  timedOut = false,
  onRetry
}, ref) => {
  const isNotice = variant === 'notice';

  const retryButton = timedOut ? (
    <Button color={isNotice ? 'inherit' : 'primary'} variant="text" size="small" onClick={onRetry} sx={retryButtonSx}>
      <FormattedMessage id="opportunities.matches.check_again"/>
    </Button>
  ) : undefined;

  const content = (
    <Box ref={isNotice ? undefined : ref} sx={contentSx}>
      {timedOut ? undefined : <CircularProgress size="1.25rem" aria-hidden/>}
      <FormattedMessage id={timedOut ? 'opportunities.matches.timed_out' : 'opportunities.matches.calculating'}/>
      {isNotice ? undefined : retryButton}
    </Box>
  );
  return isNotice ? (
    <Alert
        ref={ref}
        severity={timedOut ? 'warning' : 'info'}
        variant="standard"
        role="status"
        action={retryButton}
        sx={noticeSx}
    >
      {content}
    </Alert>
  ) : content;
});

OpportunityMatchesCalculating.displayName = 'OpportunityMatchesCalculating';

export default memo(OpportunityMatchesCalculating);
